import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fetchGameStatistics } from "@/lib/edgeFunctions";
import { useState, useEffect } from "react";

export interface GameStatistics {
  id: string;
  fixture_id: number;
  game_id: string | null;
  
  // Home team stats
  home_shots_on_goal: number | null;
  home_shots_off_goal: number | null;
  home_total_shots: number | null;
  home_blocked_shots: number | null;
  home_shots_inside_box: number | null;
  home_shots_outside_box: number | null;
  home_fouls: number | null;
  home_corners: number | null;
  home_offsides: number | null;
  home_ball_possession: number | null;
  home_yellow_cards: number | null;
  home_red_cards: number | null;
  home_saves: number | null;
  home_passes_total: number | null;
  home_passes_accurate: number | null;
  home_passes_percentage: number | null;
  
  // Expected Goals (xG)
  home_expected_goals: number | null;
  away_expected_goals: number | null;
  home_goals_prevented: number | null;
  away_goals_prevented: number | null;

  // New Sportmonks stats
  home_hit_woodwork: number | null;
  away_hit_woodwork: number | null;
  home_total_duels: number | null;
  away_total_duels: number | null;
  home_var_moments: number | null;
  away_var_moments: number | null;
  home_counter_attacks: number | null;
  away_counter_attacks: number | null;

  // Sportmonks enriched fields
  home_attacks: number | null;
  away_attacks: number | null;
  home_dangerous_attacks: number | null;
  away_dangerous_attacks: number | null;
  home_tackles: number | null;
  away_tackles: number | null;
  home_interceptions: number | null;
  away_interceptions: number | null;
  home_total_crosses: number | null;
  away_total_crosses: number | null;
  home_accurate_crosses: number | null;
  away_accurate_crosses: number | null;
  home_key_passes: number | null;
  away_key_passes: number | null;
  home_big_chances_created: number | null;
  away_big_chances_created: number | null;
  home_big_chances_missed: number | null;
  away_big_chances_missed: number | null;
  home_duels_won: number | null;
  away_duels_won: number | null;
  home_dribble_attempts: number | null;
  away_dribble_attempts: number | null;
  home_successful_dribbles: number | null;
  away_successful_dribbles: number | null;
  home_clearances: number | null;
  away_clearances: number | null;
  home_throw_ins: number | null;
  away_throw_ins: number | null;
  home_free_kicks: number | null;
  away_free_kicks: number | null;
  home_goal_kicks: number | null;
  away_goal_kicks: number | null;
  home_long_balls: number | null;
  away_long_balls: number | null;
  home_fouls_won: number | null;
  away_fouls_won: number | null;
  sportmonks_enriched_at: string | null;
  statistics_periods: Record<string, Record<string, number | null>> | null;
  
  // Away team stats
  away_shots_on_goal: number | null;
  away_shots_off_goal: number | null;
  away_total_shots: number | null;
  away_blocked_shots: number | null;
  away_shots_inside_box: number | null;
  away_shots_outside_box: number | null;
  away_fouls: number | null;
  away_corners: number | null;
  away_offsides: number | null;
  away_ball_possession: number | null;
  away_yellow_cards: number | null;
  away_red_cards: number | null;
  away_saves: number | null;
  away_passes_total: number | null;
  away_passes_accurate: number | null;
  away_passes_percentage: number | null;
  
  created_at: string;
  updated_at: string;
}

export const useGameStatistics = (fixtureId: number | null, gameStatus?: string, enablePolling = false) => {
  const [hasFetched, setHasFetched] = useState(false);
  
  const query = useQuery({
    queryKey: ['game-statistics', fixtureId],
    queryFn: async () => {
      if (!fixtureId) return null;

      const { data, error } = await supabase
        .from('game_statistics')
        .select('*')
        .eq('fixture_id', fixtureId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching game statistics:', error);
        throw error;
      }

      return data as unknown as GameStatistics | null;
    },
    enabled: !!fixtureId,
    staleTime: 30000,
    refetchInterval: gameStatus === 'live' ? 30000 : false,
  });

  // Auto-fetch statistics for finished games if not in database
  useEffect(() => {
    const shouldFetch = 
      !query.data && 
      !query.isLoading && 
      gameStatus === 'finished' && 
      !hasFetched && 
      fixtureId;

    if (shouldFetch) {
      console.log(`🔄 Auto-fetching statistics for fixture ${fixtureId}`);
      setHasFetched(true);
      
      fetchGameStatistics(fixtureId)
        .then(() => {
          console.log(`✅ Statistics fetched for fixture ${fixtureId}`);
          query.refetch();
        })
        .catch((error) => {
          console.error(`❌ Failed to fetch statistics for fixture ${fixtureId}:`, error);
        });
    }
  }, [query.data, query.isLoading, gameStatus, hasFetched, fixtureId]);

  // Auto-fetch and start polling for live games
  useEffect(() => {
    if (gameStatus === 'live' && enablePolling && fixtureId && !hasFetched) {
      console.log(`🔴 Live game detected - fetching statistics for fixture ${fixtureId}`);
      setHasFetched(true);
      
      fetchGameStatistics(fixtureId)
        .then(() => {
          console.log(`✅ Live statistics fetched for fixture ${fixtureId}`);
          query.refetch();
        })
        .catch((error) => {
          console.error(`❌ Failed to fetch live statistics for fixture ${fixtureId}:`, error);
        });
    }
  }, [gameStatus, enablePolling, fixtureId, hasFetched]);

  return query;
};

// Helper to format statistics for display
export const formatStatistics = (stats: GameStatistics | null) => {
  if (!stats) return null;

  return {
    shots: {
      home: stats.home_total_shots || 0,
      away: stats.away_total_shots || 0,
      homeOnTarget: stats.home_shots_on_goal || 0,
      awayOnTarget: stats.away_shots_on_goal || 0
    },
    shotsOffGoal: {
      home: stats.home_shots_off_goal || 0,
      away: stats.away_shots_off_goal || 0
    },
    blockedShots: {
      home: stats.home_blocked_shots || 0,
      away: stats.away_blocked_shots || 0
    },
    possession: {
      home: stats.home_ball_possession || 0,
      away: stats.away_ball_possession || 0
    },
    passes: {
      home: {
        total: stats.home_passes_total || 0,
        accurate: stats.home_passes_accurate || 0,
        percentage: stats.home_passes_percentage || 0
      },
      away: {
        total: stats.away_passes_total || 0,
        accurate: stats.away_passes_accurate || 0,
        percentage: stats.away_passes_percentage || 0
      }
    },
    fouls: {
      home: stats.home_fouls || 0,
      away: stats.away_fouls || 0
    },
    corners: {
      home: stats.home_corners || 0,
      away: stats.away_corners || 0
    },
    cards: {
      homeYellow: stats.home_yellow_cards || 0,
      homeRed: stats.home_red_cards || 0,
      awayYellow: stats.away_yellow_cards || 0,
      awayRed: stats.away_red_cards || 0
    },
    expectedGoals: {
      home: stats.home_expected_goals || 0,
      away: stats.away_expected_goals || 0
    },
    offsides: {
      home: stats.home_offsides || 0,
      away: stats.away_offsides || 0
    },
    saves: {
      home: stats.home_saves || 0,
      away: stats.away_saves || 0
    },
    shotsInsideBox: {
      home: stats.home_shots_inside_box || 0,
      away: stats.away_shots_inside_box || 0
    },
    shotsOutsideBox: {
      home: stats.home_shots_outside_box || 0,
      away: stats.away_shots_outside_box || 0
    },
    goalsPrevented: {
      home: stats.home_goals_prevented || 0,
      away: stats.away_goals_prevented || 0
    },
    // Advanced stats — show when enriched OR when live data is present
    advanced: (stats.sportmonks_enriched_at || stats.home_attacks || stats.away_attacks || stats.home_dangerous_attacks || stats.away_dangerous_attacks || stats.home_tackles || stats.away_tackles) ? {
      attacks: { home: stats.home_attacks || 0, away: stats.away_attacks || 0 },
      dangerousAttacks: { home: stats.home_dangerous_attacks || 0, away: stats.away_dangerous_attacks || 0 },
      tackles: { home: stats.home_tackles || 0, away: stats.away_tackles || 0 },
      interceptions: { home: stats.home_interceptions || 0, away: stats.away_interceptions || 0 },
      keyPasses: { home: stats.home_key_passes || 0, away: stats.away_key_passes || 0 },
      bigChancesCreated: { home: stats.home_big_chances_created || 0, away: stats.away_big_chances_created || 0 },
      bigChancesMissed: { home: stats.home_big_chances_missed || 0, away: stats.away_big_chances_missed || 0 },
      duelsWon: { home: stats.home_duels_won || 0, away: stats.away_duels_won || 0 },
      totalDuels: { home: stats.home_total_duels || 0, away: stats.away_total_duels || 0 },
      totalCrosses: { home: stats.home_total_crosses || 0, away: stats.away_total_crosses || 0 },
      accurateCrosses: { home: stats.home_accurate_crosses || 0, away: stats.away_accurate_crosses || 0 },
      dribbleAttempts: { home: stats.home_dribble_attempts || 0, away: stats.away_dribble_attempts || 0 },
      successfulDribbles: { home: stats.home_successful_dribbles || 0, away: stats.away_successful_dribbles || 0 },
      clearances: { home: stats.home_clearances || 0, away: stats.away_clearances || 0 },
      longBalls: { home: stats.home_long_balls || 0, away: stats.away_long_balls || 0 },
      throwIns: { home: stats.home_throw_ins || 0, away: stats.away_throw_ins || 0 },
      freeKicks: { home: stats.home_free_kicks || 0, away: stats.away_free_kicks || 0 },
      goalKicks: { home: stats.home_goal_kicks || 0, away: stats.away_goal_kicks || 0 },
      foulsWon: { home: stats.home_fouls_won || 0, away: stats.away_fouls_won || 0 },
      hitWoodwork: { home: stats.home_hit_woodwork || 0, away: stats.away_hit_woodwork || 0 },
      counterAttacks: { home: stats.home_counter_attacks || 0, away: stats.away_counter_attacks || 0 },
    } : null
  };
};
